import getLogger from '@ignitesales/logger-es';
import ignite from '../../../services';
import { FlowSteps } from './LoginConstants';

const logger = getLogger({ module: 'login-action-creators' });

const LoginActions = {
  BRANCHES_LOADED: 'BRANCHES_LOADED',
  BRANCH_SEARCH_STARTED: 'BRANCH_SEARCH_STARTED',
  BRANCH_SEARCH_FAILED: 'BRANCH_SEARCH_FAILED',
  EMPLOYEES_LOADED: 'EMPLOYEES_LOADED',
  EMPLOYEES_CONFLICTS_SEARCH_STARTED: 'EMPLOYEES_CONFLICTS_SEARCH_STARTED',
  EMPLOYEES_CONFLICTS_LOADED: 'EMPLOYEES_CONFLICTS_LOADED',
  EMPLOYEE_SEARCH_STARTED: 'EMPLOYEE_SEARCH_STARTED',
  EMPLOYEE_SEARCH_FAILED: 'EMPLOYEE_SEARCH_FAILED',
  EMPLOYEE_UPDATE_STARTED: 'EMPLOYEE_UPDATE_STARTED',
  EMPLOYEE_UPDATE_SUCCESSFUL: 'EMPLOYEE_UPDATE_SUCCESSFUL',
  EMPLOYEE_UPDATE_ERROR: 'EMPLOYEE_UPDATE_ERROR',
  EMPLOYEE_UPDATE_FAILED: 'EMPLOYEE_UPDATE_FAILED',
  EMPLOYEE_SELECTED: 'EMPLOYEE_SELECTED',
  EMPLOYEE_LOGGED_IN: 'EMPLOYEE_LOGGED_IN',
  EMPLOYEE_LOGIN_STARTED: 'EMPLOYEE_LOGIN_STARTED',
  EMPLOYEE_LOGIN_FAILED: 'EMPLOYEE_LOGIN_FAILED',
  LOGIN_CHECK_STARTED: 'LOGIN_CHECK_STARTED',
  LOGIN_CHECK_FINISHED: 'LOGIN_CHECK_FINISHED',
  LOGIN_CHECK_FAILED: 'LOGIN_CHECK_FAILED',
  LOGIN_FLOW_CHANGED: 'LOGIN_FLOW_CHANGED',
  LOGIN_FLOW_ENABLED: 'LOGIN_FLOW_ENABLED',
  LOGGED_OUT: 'LOGGED_OUT',
  LOG_OUT_FAILED: 'LOG_OUT_FAILED',
};

const checkIfEmployeeIsLoggedIn = () => {
  return async (dispatch, getState) => {
    try {
      dispatch(startProcessing(LoginActions.LOGIN_CHECK_STARTED));
      //check with mm-web on login status of user
      const result = await ignite.employeeLoginService.shouldEmployeeLogin();
      const { employeeId: employeeIdResult, error, isLoggedIn } = result;
      logger.debug('shouldEmployeeLogin results:', result);
      if(error) {
        logger.error('Error returned from shouldEmployeeLogin call', error);
        dispatch(loginRequestFailed(LoginActions.LOGIN_CHECK_FAILED, error));
      } else if(isLoggedIn) {
        dispatch({ type: LoginActions.EMPLOYEE_LOGGED_IN });
      } else {
        //get data from mm-web pre-selected user, if available
        const employeeLoginData = ignite.getFlashData('employeeLoginData');
        const preSelectedEmployeeId =  employeeLoginData?.selectedEmployee?.selectedEmployeeId;
        const employeeId = employeeIdResult || preSelectedEmployeeId;
        logger.debug('User is not logged in, continuing with login flow', employeeId);
        dispatch({ type: LoginActions.LOGIN_CHECK_FINISHED, employeeId, error, isLoggedIn });
        if(Object.prototype.hasOwnProperty.call(ignite, 'onLoginEmployee')) {
          logger.debug('calling custom login function');
          const shouldShowLoginModal = await ignite.onLoginEmployee({ dispatch, employeeId, state: getState() });
          shouldShowLoginModal && dispatch(enableLoginFlow(employeeId));
        } else {
          dispatch(enableLoginFlow(employeeId));
        }
      }
    } catch (ex) {
      logger.error('An error occurred while validating login status', ex);
      dispatch(loginRequestFailed(LoginActions.LOGIN_CHECK_FAILED, ex));
    }

  };
};

const enableLoginFlow = (selectedEmployeeId) => {
  return async (dispatch) => {
    if (selectedEmployeeId) {
      const employee = await ignite.employeeLoginService.get(
        selectedEmployeeId
      );
      if (employee) {
        dispatch(employeeSelected(employee));
        dispatch(onLoginFlowChange({ stepToAdd: FlowSteps.PROFILE_SELECTED }));
      }
    }
    dispatch({ type: LoginActions.LOGIN_FLOW_ENABLED });
  };
};

const onLoginFlowChange = ({ stepToAdd, stepToRemove, shouldReset }) => {
  return async (dispatch) => {
    dispatch({
      type: LoginActions.LOGIN_FLOW_CHANGED,
      stepToAdd,
      stepToRemove,
      shouldReset,
    });
  };
};

const loadEmployees = (searchValue) => {
  return async (dispatch) => {
    try {
      dispatch(startProcessing(LoginActions.EMPLOYEE_SEARCH_STARTED));
      const employees = await ignite.employeeLoginService.search(searchValue);
      dispatch(employeesLoaded(employees));
    } catch (ex) {
      logger.error('Error occurred while loading employees', ex);
      dispatch(loginRequestFailed(LoginActions.EMPLOYEE_SEARCH_FAILED, ex));
    }
  };
};

const loadEmployeeById = ({ selectedEmployeeId, thirdPartyId }) => {
  return async (dispatch) => {
    try {
      if (selectedEmployeeId || thirdPartyId) {
        logger.info('Logging in with selected employee', {
          selectedEmployeeId,
          thirdPartyId,
        });
        const employee = await ignite.employeeLoginService.get(
          selectedEmployeeId || thirdPartyId
        );
        if (employee) {
          dispatch(employeeSelected(employee));
        } else {
          logger.warn('Looking for employee, but found none');
        }
      }
    } catch (ex) {
      logger.error(
        'An error occurred while attempting to login by employee Id',
        ex
      );
      dispatch(loginRequestFailed(LoginActions.EMPLOYEE_SEARCH_FAILED, ex));
    }
  };
};

const loadBranches = (searchValue) => {
  return async (dispatch) => {
    try {
      dispatch(startProcessing(LoginActions.BRANCH_SEARCH_STARTED));
      if (searchValue.length >= 3) {
        logger.info('searching for branches', searchValue);
        const branches = await ignite.employeeLoginService.findBranches(
          searchValue
        );
        dispatch(branchesLoaded(branches));
      } else {
        dispatch(branchesLoaded([]));
      }
    } catch (ex) {
      logger.error('Error occured while searching for branches', ex);
      dispatch(loginRequestFailed(LoginActions.BRANCH_SEARCH_FAILED, ex));
    }
  };
};

const saveEmployeeProfile = (employee) => {
  return async (dispatch) => {
    try {
      dispatch(startProcessing(LoginActions.EMPLOYEE_UPDATE_STARTED));
      const result = await ignite.employeeLoginService.save(employee);

      if (result.status === 'success') {
        dispatch({
          type: LoginActions.EMPLOYEE_UPDATE_SUCCESSFUL,
          selectedEmployee: result.employee,
        });
      } else {
        if (result.errors) {
          dispatch({
            type: LoginActions.EMPLOYEE_UPDATE_ERROR,
            errors: result.errors,
          });
        }
        dispatch(loginRequestFailed(LoginActions.EMPLOYEE_UPDATE_FAILED));
      }
    } catch (ex) {
      logger.error('Error occurred while saving employee profile', ex);
      dispatch(loginRequestFailed(LoginActions.EMPLOYEE_UPDATE_FAILED, ex));
    }
  };
};

const selectEmployee = (employee) => {
  return async (dispatch) => {
    const selectedEmployee = {
      ...employee,
    };
    dispatch({ type: LoginActions.EMPLOYEE_SELECTED, selectedEmployee });
  };
};

const searchEmployeeConflicts = ({
  email: searchByEmail,
  employeeId: searchById,
}) => {
  return async (dispatch, getState) => {
    try {
      dispatch(
        startProcessing(LoginActions.EMPLOYEES_CONFLICTS_SEARCH_STARTED)
      );
      const employeeLoginState = getState().employeeLoginState;
      const currentEmployee = employeeLoginState.selectedEmployee || {};
      const result = [];
      let byEmail = null;
      if (searchByEmail) {
        byEmail = await ignite.employeeLoginService.searchByEmail(
          searchByEmail
        );
        if (byEmail && byEmail.id && byEmail.id !== currentEmployee.id) {
          byEmail.conflictType = 'email';
          result.push(byEmail);
        }
      }
      if (searchById) {
        const byId = await ignite.employeeLoginService.searchByThirdPartyId(
          searchById
        );
        if (byId && byId.id && byId.id !== currentEmployee.id) {
          byId.conflictType = 'id';
          if (!byEmail || byEmail.id !== byId.id) {
            result.push(byId);
          }
        }
      }
      dispatch({ type: LoginActions.EMPLOYEES_CONFLICTS_LOADED, result });
    } catch (ex) {
      logger.error('Error occurred while searching for conflicts', ex);
      dispatch(loginRequestFailed(LoginActions.EMPLOYEE_SEARCH_FAILED, ex));
    }
  };
};

const loginEmployee = ({ employee, branch }) => {
  return async (dispatch) => {
    try {
      dispatch(startProcessing(LoginActions.EMPLOYEE_LOGIN_STARTED));
      const result = await ignite.employeeLoginService.login({
        employee,
        branch,
      });

      if (result.status === 'success') {
        logger.info('Employee logged in', result);
        dispatch({ type: LoginActions.EMPLOYEE_LOGGED_IN, employee, branch });
      } else {
        dispatch(loginRequestFailed(LoginActions.EMPLOYEE_LOGIN_FAILED));
      }
    } catch (ex) {
      logger.error('An error occurred while logging in employee', ex);
      dispatch(loginRequestFailed(LoginActions.EMPLOYEE_LOGIN_FAILED, ex));
    }
  };
};

const logout = (shouldSkipReload) => {
  return async (dispatch) => {
    dispatch({ type: LoginActions.LOGGED_OUT });
    fetch('/logout')
      .then((response) => {
        if (!response.ok) {
          dispatch(loginRequestFailed(LoginActions.LOG_OUT_FAILED, response));
        } else if(!shouldSkipReload) {
          setTimeout(() => window.location.reload(), 500);
        }
      })
      .catch((ex) => {
        logger.error('Failed to logout', ex);
        dispatch(loginRequestFailed(LoginActions.LOG_OUT_FAILED, ex));
      });
  };
};

const startProcessing = (type) => {
  return {
    type,
  };
};

const employeesLoaded = (employees) => {
  return {
    type: LoginActions.EMPLOYEES_LOADED,
    employees,
  };
};

const employeeSelected = (selectedEmployee) => {
  return {
    type: LoginActions.EMPLOYEE_SELECTED,
    selectedEmployee,
  };
};

const branchesLoaded = (branches) => {
  return {
    type: LoginActions.BRANCHES_LOADED,
    branches,
  };
};

const loginRequestFailed = (type, error) => {
  return {
    type,
    error,
  };
};

export {
  LoginActions,
  checkIfEmployeeIsLoggedIn,
  enableLoginFlow,
  loadEmployees,
  loadEmployeeById,
  loadBranches,
  saveEmployeeProfile,
  searchEmployeeConflicts,
  selectEmployee,
  loginEmployee,
  onLoginFlowChange,
  logout,
};
